import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';

// Hook to detect landscape orientation
function useIsLandscape() {
  const [isLandscape, setIsLandscape] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth > window.innerHeight;
    }
    return false;
  });

  useEffect(() => {
    const checkOrientation = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };

    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  return isLandscape;
}

// Floating control panel for TV display
function TVControls({ gameId, kioskMode, zoomLevel, setZoomLevel }) {
  const [showControls, setShowControls] = useState(true);
  const navigate = useNavigate();

  // Auto-enter fullscreen on mount with cross-browser support
  useEffect(() => {
    const enterFullscreen = async () => {
      const elem = document.documentElement;

      // Check if already in fullscreen
      const isFullscreen = document.fullscreenElement ||
                          document.webkitFullscreenElement ||
                          document.mozFullScreenElement ||
                          document.msFullscreenElement;

      if (isFullscreen) return;

      try {
        // Try standard API first
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
        }
        // Safari (iOS and macOS)
        else if (elem.webkitRequestFullscreen) {
          await elem.webkitRequestFullscreen();
        }
        // Firefox
        else if (elem.mozRequestFullScreen) {
          await elem.mozRequestFullScreen();
        }
        // IE/Edge (older)
        else if (elem.msRequestFullscreen) {
          await elem.msRequestFullscreen();
        }
      } catch (err) {
        // Fullscreen may be blocked by browser - that's ok
        // This often happens if not triggered by user gesture
        console.log('Fullscreen not available:', err);
      }
    };

    // Small delay to ensure page is loaded
    const timeout = setTimeout(enterFullscreen, 100);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    // Auto-hide controls after 3 seconds of inactivity (longer in kiosk mode)
    let timeout;
    const hideDelay = kioskMode ? 5000 : 3000;
    const handleMouseMove = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => setShowControls(false), hideDelay);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchstart', handleMouseMove);

    // Initial hide
    timeout = setTimeout(() => setShowControls(false), hideDelay);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchstart', handleMouseMove);
      clearTimeout(timeout);
    };
  }, [kioskMode]);

  const exitTVMode = async () => {
    // Exit fullscreen if active (cross-browser)
    try {
      const isFullscreen = document.fullscreenElement ||
                          document.webkitFullscreenElement ||
                          document.mozFullScreenElement ||
                          document.msFullscreenElement;

      if (isFullscreen) {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          await document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
      }
    } catch (err) {
      console.log('Error exiting fullscreen:', err);
    }
    // Navigate back to the game
    navigate(`/game/${gameId}`);
  };

  const toggleZoom = () => {
    // Cycle through zoom levels: 1 -> 0.85 -> 0.7 -> 1
    if (zoomLevel === 1) setZoomLevel(0.85);
    else if (zoomLevel === 0.85) setZoomLevel(0.7);
    else setZoomLevel(1);
  };

  // In kiosk mode, only show minimal controls
  if (kioskMode) {
    return (
      <>
        {/* Kiosk mode indicator - small, unobtrusive */}
        <div
          className={`fixed top-4 right-4 transition-opacity duration-300 z-50 ${
            showControls ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="bg-black/50 text-white/70 px-3 py-1 rounded-full text-sm backdrop-blur-sm">
            Auto-refreshing
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Zoom button - top left */}
      <div
        className={`fixed top-4 left-4 transition-opacity duration-300 z-50 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <button
          onClick={toggleZoom}
          className="bg-black/70 hover:bg-black/90 text-white w-12 h-12 rounded-xl text-xl font-medium backdrop-blur-sm border border-white/20 flex items-center justify-center"
          title={zoomLevel === 1 ? 'Zoom out' : 'Reset zoom'}
        >
          {zoomLevel === 1 ? '⊖' : zoomLevel === 0.85 ? '⊖' : '⊕'}
        </button>
      </div>

      {/* Exit TV Mode button */}
      <div
        className={`fixed bottom-6 right-6 transition-opacity duration-300 z-50 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <button
          onClick={exitTVMode}
          className="bg-black/70 hover:bg-black/90 text-white px-4 py-3 rounded-xl text-lg font-medium backdrop-blur-sm border border-white/20 flex items-center gap-2"
          title="Exit TV Mode"
        >
          ← Exit TV Mode
        </button>
      </div>
    </>
  );
}

const FIVE_CROWNS_ROUNDS = ['3s', '4s', '5s', '6s', '7s', '8s', '9s', '10s', 'Js', 'Qs', 'Ks'];
const PHASE_10_PHASES = [
  '2 sets of 3',
  '1 set of 3 + 1 run of 4',
  '1 set of 4 + 1 run of 4',
  '1 run of 7',
  '1 run of 8',
  '1 run of 9',
  '2 sets of 4',
  '7 cards of one color',
  '1 set of 5 + 1 set of 2',
  '1 set of 5 + 1 set of 3'
];

export default function GameTVDisplay() {
  const { activeGameId } = useParams();
  const [searchParams] = useSearchParams();
  const kioskMode = searchParams.get('kiosk') === '1';
  const [activeGame, setActiveGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set body background to prevent white edges
  useEffect(() => {
    const originalBackground = document.body.style.backgroundColor;
    const originalOverflow = document.body.style.overflow;
    const originalMargin = document.body.style.margin;
    const originalHtmlBackground = document.documentElement.style.backgroundColor;

    // Set dark backgrounds on html and body to prevent any white showing
    document.documentElement.style.backgroundColor = '#022c22'; // emerald-950
    document.body.style.backgroundColor = '#022c22';
    document.body.style.overflow = 'hidden';
    document.body.style.margin = '0';

    return () => {
      document.documentElement.style.backgroundColor = originalHtmlBackground;
      document.body.style.backgroundColor = originalBackground;
      document.body.style.overflow = originalOverflow;
      document.body.style.margin = originalMargin;
    };
  }, []);

  useEffect(() => {
    fetchActiveGame();

    // Connect to Socket.io for real-time updates
    const socket = io(window.API_URL, {
      transports: ['websocket', 'polling']
    });

    socket.on('connect', () => {
      socket.emit('join-game', { gameCode: activeGameId });
    });

    socket.on('game-state', (state) => {
      setActiveGame(prev => prev ? { ...prev, game_state: state } : prev);
    });

    socket.on('score-update', (newScores) => {
      setActiveGame(prev => prev ? {
        ...prev,
        game_state: { ...prev.game_state, scores: newScores }
      } : prev);
    });

    // Poll for updates as backup
    const interval = setInterval(fetchActiveGame, 2000);

    return () => {
      socket.emit('leave-game', { gameCode: activeGameId });
      socket.disconnect();
      clearInterval(interval);
    };
  }, [activeGameId]);

  const fetchActiveGame = async () => {
    try {
      const res = await fetch(`${window.API_URL}/api/games/active/${activeGameId}`);
      if (!res.ok) {
        setError('Game not found');
        setLoading(false);
        return;
      }
      const data = await res.json();
      setActiveGame(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching active game:', err);
      setError('Failed to load game');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <p className="text-white text-2xl">Loading game...</p>
      </div>
    );
  }

  if (error || !activeGame) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <p className="text-red-400 text-2xl">{error || 'Game not found'}</p>
      </div>
    );
  }

  // Route to appropriate TV display based on game type
  if (activeGame.slug === 'five-crowns') {
    return <FiveCrownsTVContent activeGame={activeGame} gameId={activeGameId} kioskMode={kioskMode} />;
  } else if (activeGame.slug === 'phase-10') {
    return <Phase10TVContent activeGame={activeGame} gameId={activeGameId} kioskMode={kioskMode} />;
  }

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
      <p className="text-white text-2xl">TV display not available for {activeGame.game_name}</p>
    </div>
  );
}

function FiveCrownsTVContent({ activeGame, gameId, kioskMode }) {
  const isLandscape = useIsLandscape();
  const [zoomLevel, setZoomLevel] = useState(1);
  const players = activeGame.players || [];
  const scores = activeGame.game_state?.scores || players.map(() => Array(11).fill(''));
  const foundDealerIndex = players.findIndex(p => p.is_dealer);
  const dealerIndex = foundDealerIndex >= 0 ? foundDealerIndex : 0;

  const getTotal = (playerIndex) => {
    return scores[playerIndex]?.reduce((sum, score) => sum + (parseInt(score) || 0), 0) || 0;
  };

  const hasAnyScores = () => {
    return players.some((_, i) => scores[i]?.some(s => s !== ''));
  };

  const isGameComplete = () => {
    return players.every((_, p) =>
      FIVE_CROWNS_ROUNDS.every((_, r) => scores[p]?.[r] !== '' && scores[p]?.[r] !== undefined)
    );
  };

  const getLeaderboard = () => {
    const playerData = players.map((player, i) => ({
      ...player,
      total: getTotal(i),
      index: i
    }));

    if (!hasAnyScores()) {
      return playerData;
    }

    return playerData.sort((a, b) => a.total - b.total);
  };

  const getCurrentRound = () => {
    for (let r = 0; r < FIVE_CROWNS_ROUNDS.length; r++) {
      const allFilled = players.every((_, p) => scores[p]?.[r] !== '' && scores[p]?.[r] !== undefined);
      if (!allFilled) return r;
    }
    return FIVE_CROWNS_ROUNDS.length;
  };

  const getMedal = (rank) => {
    if (rank === 0) return '🥇';
    if (rank === 1) return '🥈';
    if (rank === 2) return '🥉';
    return `#${rank + 1}`;
  };

  const getRank = (playerIndex, leaderboard) => {
    if (!hasAnyScores()) return null;
    const playerTotal = getTotal(playerIndex);
    let rank = 0;
    let prevTotal = null;
    for (let i = 0; i < leaderboard.length; i++) {
      if (leaderboard[i].total !== prevTotal) {
        rank = i;
        prevTotal = leaderboard[i].total;
      }
      if (leaderboard[i].index === playerIndex) {
        return rank;
      }
    }
    return 0;
  };

  const leaderboard = getLeaderboard();
  const currentRound = getCurrentRound();
  const currentDealer = (dealerIndex + currentRound) % players.length;
  const gameHasScores = hasAnyScores();
  const gameComplete = isGameComplete();
  const winners = gameComplete ? leaderboard.filter(p => p.total === leaderboard[0]?.total) : [];

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-emerald-900 via-emerald-950 to-black overflow-hidden">
      <TVControls gameId={gameId} kioskMode={kioskMode} zoomLevel={zoomLevel} setZoomLevel={setZoomLevel} />
      <div
        className={`w-full h-full p-4 flex ${isLandscape ? 'p-8 flex-row' : 'flex-col'}`}
        style={{
          transform: `scale(${zoomLevel})`,
          transformOrigin: 'top left',
          width: `${100 / zoomLevel}%`,
          height: `${100 / zoomLevel}%`,
          overflow: 'auto'
        }}
      >
        {/* Main Scorecard - Left Side on landscape, Top on portrait */}
        <div className={`flex-1 flex flex-col ${isLandscape ? 'pr-6 mb-0' : 'mb-4'}`}>
          {/* Header */}
          <div className={`text-center ${isLandscape ? 'mb-6' : 'mb-4'}`}>
            <h1 className={`font-bold text-amber-400 ${isLandscape ? 'text-6xl mb-2' : 'text-3xl mb-1'}`}>👑 Five Crowns 👑</h1>
            <p className={`text-emerald-300 ${isLandscape ? 'text-2xl' : 'text-lg'}`}>
              {gameComplete ? '🎉 Game Complete!' : `Round: ${FIVE_CROWNS_ROUNDS[currentRound]} • 🃏 ${players[currentDealer]?.name} dealing`}
          </p>
        </div>

        {/* Winner Banner */}
        {gameComplete && winners.length > 0 && (
          <div className={`bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-center animate-pulse ${isLandscape ? 'rounded-2xl p-6 mb-6' : 'rounded-xl p-4 mb-4'}`}>
            <div className={`${isLandscape ? 'text-5xl mb-2' : 'text-3xl mb-1'}`}>👑🎉👑</div>
            {winners.length === 1 ? (
              <>
                <div className={`text-emerald-950 font-bold ${isLandscape ? 'text-4xl' : 'text-2xl'}`}>{winners[0].name} WINS!</div>
                <div className={`text-emerald-900 ${isLandscape ? 'text-2xl' : 'text-lg'}`}>Final Score: {winners[0].total} points</div>
              </>
            ) : (
              <>
                <div className={`text-emerald-950 font-bold ${isLandscape ? 'text-4xl' : 'text-2xl'}`}>TIE GAME!</div>
                <div className={`text-emerald-900 ${isLandscape ? 'text-2xl' : 'text-lg'}`}>
                  {winners.map(w => w.name).join(' & ')} tied with {winners[0].total} points!
                </div>
              </>
            )}
          </div>
        )}

        {/* Score Table */}
        <div className={`overflow-auto bg-emerald-950 border border-emerald-700/50 ${isLandscape ? 'rounded-2xl mx-16' : 'rounded-xl mx-4'}`}>
          <table className={`${isLandscape ? 'text-xl' : 'text-sm'}`} style={{ borderSpacing: 0, borderCollapse: 'collapse' }}>
            <thead className="sticky top-0 z-10">
              <tr className="bg-emerald-900">
                <th className={`text-amber-400 font-bold text-left bg-emerald-900 ${isLandscape ? 'p-4 text-2xl' : 'p-2 text-base'}`} style={{ width: isLandscape ? '80px' : '48px', minWidth: isLandscape ? '80px' : '48px' }}>Wild</th>
                {players.map((player, i) => {
                  const rank = getRank(i, leaderboard);
                  const isDealer = currentDealer === i && !gameComplete;
                  const isLeader = rank === 0 && gameHasScores;
                  const isWinner = winners.some(w => w.index === i);
                  // Fixed column width: avatar(32px) + gap(8px) + icons(~48px) + name(~80px) + padding
                  const colWidth = isLandscape ? '160px' : '90px';
                  return (
                    <th
                      key={player.user_id}
                      className={`text-center bg-emerald-900 ${isLandscape ? 'p-3' : 'p-2'} ${
                        isDealer ? 'bg-amber-500/30' : ''
                      } ${isWinner && gameComplete ? 'bg-amber-500/40' : ''}`}
                      style={{ width: colWidth, minWidth: colWidth, maxWidth: colWidth }}
                    >
                      <div className="flex items-center justify-center gap-1 whitespace-nowrap">
                        <div
                          className={`rounded-full flex items-center justify-center font-bold flex-shrink-0 ${isLandscape ? 'w-8 h-8 text-sm' : 'w-5 h-5 text-xs'}`}
                          style={{ backgroundColor: player.avatar_color || '#6366f1' }}
                        >
                          {player.name?.charAt(0)}
                        </div>
                        <span className={`font-bold truncate ${isLandscape ? 'text-lg' : 'text-xs'} ${
                          isWinner && gameComplete ? 'text-amber-300' :
                          isDealer ? 'text-amber-300' :
                          isLeader ? 'text-amber-300' : 'text-white'
                        }`}>
                          {isWinner && gameComplete ? '👑' : isDealer ? '🃏' : ''}{isLeader && !gameComplete ? '👑' : ''}{player.name?.split(' ')[0]}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {FIVE_CROWNS_ROUNDS.map((round, ri) => (
                <tr
                  key={round}
                  className={`border-t border-emerald-700/30 ${
                    ri === currentRound && !gameComplete ? 'bg-emerald-700/30' : ''
                  }`}
                >
                  <td className={`font-bold text-left bg-emerald-950 ${isLandscape ? 'p-4 text-xl' : 'p-2 text-sm'} ${
                    ri === currentRound && !gameComplete ? 'text-amber-200 bg-emerald-800' : 'text-amber-300'
                  }`} style={{ width: isLandscape ? '80px' : '48px', minWidth: isLandscape ? '80px' : '48px' }}>
                    {ri === currentRound && !gameComplete ? '▶ ' : ''}{round}
                  </td>
                  {players.map((player, pi) => {
                    const scoreValue = scores[pi]?.[ri] || '';
                    const isZero = scoreValue === '0';
                    const isWinner = winners.some(w => w.index === pi);
                    const colWidth = isLandscape ? '160px' : '90px';
                    return (
                      <td
                        key={player.user_id}
                        className={`text-center font-mono bg-emerald-950 ${isLandscape ? 'p-3 text-2xl' : 'p-2 text-base'} ${
                          isWinner && gameComplete ? 'bg-amber-500/10' : ''
                        }`}
                        style={{ width: colWidth, minWidth: colWidth, maxWidth: colWidth }}
                      >
                        <span className={isZero ? 'text-green-400 font-bold' : 'text-white'}>
                          {scoreValue || '–'}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {/* Totals Row */}
              <tr className="bg-emerald-900 font-bold border-t-4 border-emerald-600 sticky bottom-0">
                <td className={`text-amber-400 bg-emerald-900 ${isLandscape ? 'p-4 text-2xl' : 'p-2 text-base'}`} style={{ width: isLandscape ? '80px' : '48px', minWidth: isLandscape ? '80px' : '48px' }}>TOTAL</td>
                {players.map((player, pi) => {
                  const rank = getRank(pi, leaderboard);
                  const isLeader = rank === 0 && gameHasScores;
                  const isWinner = winners.some(w => w.index === pi);
                  const colWidth = isLandscape ? '160px' : '90px';
                  return (
                    <td
                      key={player.user_id}
                      className={`text-center bg-emerald-900 ${isLandscape ? 'p-3 text-3xl' : 'p-2 text-lg'} ${
                        isWinner && gameComplete ? 'bg-amber-500/20' : ''
                      } ${isLeader ? 'text-amber-300' : 'text-white'}`}
                      style={{ width: colWidth, minWidth: colWidth, maxWidth: colWidth }}
                    >
                      {getTotal(pi)}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Leaderboard - Right Side on landscape, hidden on portrait */}
      {isLandscape && (
        <div className="w-96 flex flex-col">
          <div className="bg-emerald-950/80 rounded-2xl p-6 border border-emerald-700/50 flex-1 flex flex-col">
            <h2 className="text-4xl font-bold text-amber-400 mb-6 text-center">🏆 STANDINGS</h2>

            {/* Top 3 Podium - Large */}
            <div className="space-y-4 mb-6">
              {leaderboard.slice(0, 3).map((player, i) => {
                // Calculate actual rank based on ties (same total = same rank)
                let actualRank = 0;
                let prevTotal = null;
                for (let j = 0; j <= i; j++) {
                  if (leaderboard[j].total !== prevTotal) {
                    actualRank = j;
                    prevTotal = leaderboard[j].total;
                  }
                }
                const isWinner = winners.some(w => w.index === player.index);
                // Check if tied with adjacent players
                const prevPlayer = leaderboard[i - 1];
                const nextPlayer = leaderboard[i + 1];
                const isTied = gameHasScores && (
                  (prevPlayer && prevPlayer.total === player.total) ||
                  (nextPlayer && nextPlayer.total === player.total)
                );

                return (
                  <div
                    key={player.user_id}
                    className={`flex items-center justify-between px-6 py-5 rounded-xl transition-all ${
                      !gameHasScores ? 'bg-emerald-800/30' :
                      isWinner && gameComplete ? 'bg-amber-500/40 border-2 border-amber-400 animate-pulse' :
                      actualRank === 0 ? 'bg-amber-500/30 border border-amber-500/50' :
                      actualRank === 1 ? 'bg-gray-400/20 border border-gray-400/30' :
                      actualRank === 2 ? 'bg-orange-600/20 border border-orange-500/30' :
                      'bg-emerald-800/30'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-5xl">
                        {!gameHasScores ? `${i + 1}.` : isWinner && gameComplete ? '👑' : getMedal(actualRank)}
                      </span>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold"
                          style={{ backgroundColor: player.avatar_color || '#6366f1' }}
                        >
                          {player.name?.charAt(0)}
                        </div>
                        <div className="flex flex-col">
                          <span className={`text-2xl font-bold ${
                            actualRank === 0 && gameHasScores ? 'text-amber-300' : 'text-white'
                          }`}>
                            {player.name}
                          </span>
                          {isTied && <span className="text-sm text-emerald-400">(tied)</span>}
                        </div>
                      </div>
                    </div>
                    <span className={`text-4xl font-bold font-mono ${
                      actualRank === 0 && gameHasScores ? 'text-amber-300' : 'text-white'
                    }`}>
                      {gameHasScores ? player.total : '–'}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* 4th place and beyond - Scrollable, smaller */}
            {leaderboard.length > 3 && (
              <div className="flex-1 overflow-auto">
                <div className="space-y-2">
                  {leaderboard.slice(3).map((player, i) => {
                    // Calculate actual rank for players beyond top 3
                    let actualRank = 0;
                    let prevTotal = null;
                    for (let j = 0; j <= i + 3; j++) {
                      if (leaderboard[j].total !== prevTotal) {
                        actualRank = j;
                        prevTotal = leaderboard[j].total;
                      }
                    }
                    return (
                      <div
                        key={player.user_id}
                        className="flex items-center justify-between px-4 py-3 rounded-lg bg-emerald-800/30"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl text-emerald-400">#{actualRank + 1}</span>
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                            style={{ backgroundColor: player.avatar_color || '#6366f1' }}
                          >
                            {player.name?.charAt(0)}
                          </div>
                          <span className="text-xl text-white">{player.name}</span>
                        </div>
                        <span className="text-2xl font-mono text-white">
                          {gameHasScores ? player.total : '–'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-4 text-center text-emerald-400 text-lg">
            J=11 • Q=12 • K=13 • Joker=50
          </div>
        </div>
      )}

      {/* Portrait Footer */}
      {!isLandscape && (
        <div className="text-center text-emerald-400 text-sm mt-2">
          J=11 • Q=12 • K=13 • Joker=50
        </div>
      )}
    </div>
  </div>
  );
}

function Phase10TVContent({ activeGame, gameId, kioskMode }) {
  const isLandscape = useIsLandscape();
  const [zoomLevel, setZoomLevel] = useState(1);
  const players = activeGame.players || [];
  const gameState = activeGame.game_state || {};
  const scores = gameState.scores || players.map(() => Array(10).fill(''));
  const phaseOverrides = gameState.phaseOverrides || {};
  const foundDealerIndex = players.findIndex(p => p.is_dealer);
  const dealerIndex = foundDealerIndex >= 0 ? foundDealerIndex : 0;

  const calculatePhase = (playerIndex) => {
    if (phaseOverrides[playerIndex] !== undefined) {
      return phaseOverrides[playerIndex];
    }

    let phase = 1;
    for (let r = 0; r < (scores[playerIndex]?.length || 0); r++) {
      const score = parseInt(scores[playerIndex]?.[r]);
      if (!isNaN(score) && score < 50) {
        phase = Math.min(10, phase + 1);
      }
    }
    return phase;
  };

  const getTotal = (playerIndex) => {
    return (scores[playerIndex] || []).reduce((sum, s) => sum + (parseInt(s) || 0), 0);
  };

  const hasAnyScores = () => {
    return players.some((_, i) => scores[i]?.some(s => s !== ''));
  };

  const getCurrentPhases = () => {
    return players.map((_, i) => calculatePhase(i));
  };

  const getLeaderboard = () => {
    const currentPhases = getCurrentPhases();
    const playerData = players.map((player, i) => ({
      ...player,
      total: getTotal(i),
      phase: currentPhases[i],
      index: i
    }));

    if (!hasAnyScores()) {
      return playerData;
    }

    return playerData.sort((a, b) => {
      if (b.phase !== a.phase) return b.phase - a.phase;
      return a.total - b.total;
    });
  };

  const getMedal = (rank) => ['🥇', '🥈', '🥉'][rank] || `#${rank + 1}`;

  const currentPhases = getCurrentPhases();
  const currentRound = Math.max(0, (scores[0] || []).findIndex(s => s === ''));
  const currentDealer = (dealerIndex + currentRound) % players.length;
  const gameHasScores = hasAnyScores();
  const leaderboard = getLeaderboard();

  // Check for winner (someone who has completed 10 phases)
  let winner = null;
  players.forEach((_, pi) => {
    let completedPhases = 0;
    for (const s of (scores[pi] || [])) {
      const score = parseInt(s);
      if (!isNaN(score) && score < 50) completedPhases++;
    }
    if (completedPhases >= 10) {
      winner = pi;
    }
  });

  const rounds = 10;

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-blue-950 via-blue-900 to-cyan-900 overflow-hidden">
      <TVControls gameId={gameId} kioskMode={kioskMode} zoomLevel={zoomLevel} setZoomLevel={setZoomLevel} />
      <div
        className={`w-full h-full p-4 flex ${isLandscape ? 'p-8 flex-row' : 'flex-col'}`}
        style={{
          transform: `scale(${zoomLevel})`,
          transformOrigin: 'top left',
          width: `${100 / zoomLevel}%`,
          height: `${100 / zoomLevel}%`,
          overflow: 'auto'
        }}
      >
        {/* Main Scorecard - Left Side on landscape, Top on portrait */}
        <div className={`flex-1 flex flex-col ${isLandscape ? 'pr-6 mb-0' : 'mb-4'}`}>
          {/* Header */}
          <div className={`text-center ${isLandscape ? 'mb-6' : 'mb-4'}`}>
            <h1 className={`font-bold text-cyan-300 ${isLandscape ? 'text-6xl mb-2' : 'text-3xl mb-1'}`}>🎯 Phase 10 🎯</h1>
            <p className={`text-blue-300 ${isLandscape ? 'text-2xl' : 'text-lg'}`}>
              {winner !== null ? '🎉 Game Complete!' : `Round ${currentRound + 1} of ${rounds} • 🃏 ${players[currentDealer]?.name} shuffling`}
            </p>
          </div>

          {/* Winner Banner */}
          {winner !== null && (
            <div className={`bg-gradient-to-r from-yellow-500 via-amber-400 to-yellow-500 text-center animate-pulse ${isLandscape ? 'rounded-2xl p-6 mb-6' : 'rounded-xl p-4 mb-4'}`}>
              <div className={`${isLandscape ? 'text-5xl mb-2' : 'text-3xl mb-1'}`}>🏆🎉🏆</div>
              <div className={`text-blue-950 font-bold ${isLandscape ? 'text-4xl' : 'text-2xl'}`}>{players[winner]?.name} WINS!</div>
              <div className={`text-blue-900 ${isLandscape ? 'text-2xl' : 'text-lg'}`}>Completed all 10 phases with {getTotal(winner)} points</div>
            </div>
          )}

        {/* Phase Display Row */}
        <div className={`bg-blue-800/50 rounded-xl border border-blue-600/30 ${isLandscape ? 'p-4 mb-4' : 'p-3 mb-3'}`}>
          <div className={`flex justify-center flex-wrap ${isLandscape ? 'gap-4' : 'gap-2'}`}>
            {players.map((player, pi) => (
              <div
                key={player.user_id}
                className={`flex flex-col items-center ${isLandscape ? 'rounded-xl p-4 min-w-[120px]' : 'rounded-lg p-2 min-w-[80px]'} ${
                  currentDealer === pi ? 'bg-amber-500/30 border-2 border-amber-400/50' :
                  winner === pi ? 'bg-yellow-500/30 border-2 border-yellow-400' :
                  leaderboard[0]?.index === pi ? 'bg-cyan-500/20 border border-cyan-400/30' :
                  'bg-blue-900/50'
                }`}
              >
                <div className={`flex items-center ${isLandscape ? 'gap-2' : 'gap-1'}`}>
                  <div
                    className={`rounded-full flex items-center justify-center text-xs font-bold ${isLandscape ? 'w-6 h-6' : 'w-5 h-5'}`}
                    style={{ backgroundColor: player.avatar_color || '#6366f1' }}
                  >
                    {player.name?.charAt(0)}
                  </div>
                  <span className={`text-blue-200 font-medium truncate ${isLandscape ? 'text-lg max-w-[100px]' : 'text-xs max-w-[60px]'}`}>
                    {currentDealer === pi ? '🃏 ' : ''}{winner === pi ? '🏆 ' : ''}{player.name?.split(' ')[0]}
                  </span>
                </div>
                <span className={`font-bold text-cyan-300 mt-1 ${isLandscape ? 'text-4xl' : 'text-2xl'}`}>P{currentPhases[pi]}</span>
                {isLandscape && (
                  <span className="text-sm text-blue-300 mt-1 text-center">{PHASE_10_PHASES[currentPhases[pi] - 1]}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Score Table */}
        <div className={`overflow-auto bg-blue-950 border border-blue-700/50 ${isLandscape ? 'rounded-2xl mx-16' : 'rounded-xl mx-4'}`}>
          <table className={`${isLandscape ? 'text-xl' : 'text-sm'}`} style={{ borderSpacing: 0, borderCollapse: 'collapse' }}>
            <thead className="sticky top-0 z-10">
              <tr className="bg-blue-800">
                <th className={`text-cyan-300 font-bold text-left bg-blue-800 ${isLandscape ? 'p-4 text-2xl' : 'p-2 text-base'}`} style={{ width: isLandscape ? '80px' : '48px', minWidth: isLandscape ? '80px' : '48px' }}>Rnd</th>
                {players.map((player, i) => {
                  const colWidth = isLandscape ? '160px' : '90px';
                  return (
                    <th
                      key={player.user_id}
                      className={`text-center bg-blue-800 ${isLandscape ? 'p-3' : 'p-2'} ${
                        currentDealer === i ? 'bg-amber-500/30' : ''
                      } ${winner === i ? 'bg-yellow-500/30' : ''}`}
                      style={{ width: colWidth, minWidth: colWidth, maxWidth: colWidth }}
                    >
                      <div className="flex items-center justify-center gap-1 whitespace-nowrap">
                        <div
                          className={`rounded-full flex items-center justify-center font-bold flex-shrink-0 ${isLandscape ? 'w-8 h-8 text-sm' : 'w-5 h-5 text-xs'}`}
                          style={{ backgroundColor: player.avatar_color || '#6366f1' }}
                        >
                          {player.name?.charAt(0)}
                        </div>
                        <span className={`font-bold truncate ${isLandscape ? 'text-lg' : 'text-xs'} ${
                          currentDealer === i ? 'text-amber-300' :
                          winner === i ? 'text-yellow-300' :
                          leaderboard[0]?.index === i ? 'text-cyan-300' : 'text-white'
                        }`}>
                          {currentDealer === i ? '🃏' : ''}{winner === i ? '🏆' : ''}{player.name?.split(' ')[0]}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rounds }, (_, ri) => {
                return (
                  <tr
                    key={ri}
                    className={`border-t border-blue-700/30 ${
                      ri === currentRound ? 'bg-blue-700/30' : ''
                    }`}
                  >
                    <td className={`font-bold text-left bg-blue-950 ${isLandscape ? 'p-4 text-xl' : 'p-2 text-sm'} ${
                      ri === currentRound ? 'text-cyan-200 bg-blue-800' : 'text-cyan-400'
                    }`} style={{ width: isLandscape ? '80px' : '48px', minWidth: isLandscape ? '80px' : '48px' }}>
                      {ri === currentRound ? '▶ ' : ''}{ri + 1}
                    </td>
                    {players.map((player, pi) => {
                      const score = parseInt(scores[pi]?.[ri]);
                      const completed = !isNaN(score) && score < 50;
                      const colWidth = isLandscape ? '160px' : '90px';
                      return (
                        <td
                          key={player.user_id}
                          className={`text-center font-mono bg-blue-950 ${isLandscape ? 'p-3 text-2xl' : 'p-2 text-base'}`}
                          style={{ width: colWidth, minWidth: colWidth, maxWidth: colWidth }}
                        >
                          <span className={completed ? 'text-green-400 font-bold' : 'text-white'}>
                            {scores[pi]?.[ri] || '–'}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {/* Totals Row */}
              <tr className="bg-blue-900 font-bold border-t-4 border-blue-600 sticky bottom-0">
                <td className={`text-cyan-400 bg-blue-900 ${isLandscape ? 'p-4 text-2xl' : 'p-2 text-base'}`} style={{ width: isLandscape ? '80px' : '48px', minWidth: isLandscape ? '80px' : '48px' }}>TOT</td>
                {players.map((player, pi) => {
                  const rank = leaderboard.findIndex(p => p.index === pi);
                  const colWidth = isLandscape ? '160px' : '90px';
                  return (
                    <td
                      key={player.user_id}
                      className={`text-center bg-blue-900 ${isLandscape ? 'p-3 text-3xl' : 'p-2 text-lg'} ${
                        winner === pi ? 'text-yellow-300' :
                        rank === 0 && leaderboard.length > 0 ? 'text-cyan-300' : 'text-white'
                      }`}
                      style={{ width: colWidth, minWidth: colWidth, maxWidth: colWidth }}
                    >
                      {getTotal(pi)}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Leaderboard - Right Side on landscape, hidden on portrait */}
      {isLandscape && (
          <div className="w-96 flex flex-col">
            <div className="bg-blue-800/50 rounded-2xl p-6 border border-blue-600/30 flex-1 flex flex-col">
              <h2 className="text-4xl font-bold text-cyan-300 mb-6 text-center">🏆 LEADERBOARD</h2>

              {/* Top 3 Podium - Large */}
              <div className="space-y-4 mb-6">
                {leaderboard.slice(0, 3).map((player, i) => {
                  const isWinner = winner === player.index;

                  return (
                    <div
                      key={player.user_id}
                      className={`flex items-center justify-between px-6 py-5 rounded-xl transition-all ${
                        !gameHasScores ? 'bg-blue-900/30' :
                        isWinner ? 'bg-yellow-500/40 border-2 border-yellow-400 animate-pulse' :
                        i === 0 ? 'bg-cyan-500/30 border border-cyan-400/50' :
                        i === 1 ? 'bg-gray-400/20 border border-gray-400/30' :
                        i === 2 ? 'bg-orange-600/20 border border-orange-500/30' :
                        'bg-blue-900/30'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <span className="text-5xl">
                          {!gameHasScores ? `${i + 1}.` : isWinner ? '🏆' : getMedal(i)}
                        </span>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold"
                              style={{ backgroundColor: player.avatar_color || '#6366f1' }}
                            >
                              {player.name?.charAt(0)}
                            </div>
                            <span className={`text-2xl font-bold ${
                              i === 0 && gameHasScores ? 'text-cyan-300' : 'text-white'
                            }`}>
                              {player.name}
                            </span>
                          </div>
                          <span className="text-lg text-blue-300">Phase {player.phase}</span>
                        </div>
                      </div>
                      <span className={`text-3xl font-bold font-mono ${
                        i === 0 && gameHasScores ? 'text-cyan-300' : 'text-white'
                      }`}>
                        {gameHasScores ? player.total : '–'}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* 4th place and beyond - Scrollable, smaller */}
              {leaderboard.length > 3 && (
                <div className="flex-1 overflow-auto">
                  <div className="space-y-2">
                    {leaderboard.slice(3).map((player, i) => (
                      <div
                        key={player.user_id}
                        className="flex items-center justify-between px-4 py-3 rounded-lg bg-blue-900/30"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl text-blue-400">#{i + 4}</span>
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                                style={{ backgroundColor: player.avatar_color || '#6366f1' }}
                              >
                                {player.name?.charAt(0)}
                              </div>
                              <span className="text-lg text-white">{player.name}</span>
                            </div>
                            <span className="text-sm text-blue-400">Phase {player.phase}</span>
                          </div>
                        </div>
                        <span className="text-xl font-mono text-white">{player.total}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Phase Reference */}
            <div className="mt-4 bg-blue-800/30 rounded-xl p-4 border border-blue-700/30">
              <h3 className="text-cyan-300 text-lg font-bold mb-2 text-center">📋 PHASES</h3>
              <div className="grid grid-cols-2 gap-1 text-sm">
                {PHASE_10_PHASES.map((desc, i) => (
                  <div
                    key={i}
                    className={`flex gap-2 p-1 rounded ${
                      currentPhases.some(p => p === i + 1) ? 'bg-cyan-500/20 text-cyan-200' : 'text-blue-300'
                    }`}
                  >
                    <span className="text-cyan-400 font-bold w-4">{i + 1}.</span>
                    <span className="text-xs">{desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
